import { Slot } from '../Modules/Slot/Model/Slot';
import { User } from '../Modules/User/Model/User';
import { isSlotOperationRestricted, getRestrictionMessage } from './timeRestriction';
import {
  parseCalendarDate,
  startOfTodayLocal,
  isSameLocalCalendarDay,
} from '../Modules/Slot/Domain/UseCase/Utils/DateUtil';

function isAdpa99Username(username?: string | null): boolean {
  if (!username) return false;
  const normalized = username.toLowerCase();
  const withoutSuffix = normalized.endsWith('@lift')
    ? normalized.slice(0, -'@lift'.length)
    : normalized.endsWith('@stizen.g')
      ? normalized.slice(0, -'@stizen.g'.length)
      : normalized;
  return withoutSuffix === 'adpa99';
}

export interface SlotPermissionResult {
  canExtend: boolean;
  canEdit: boolean;
  canBatchEdit: boolean;
  canRefund: boolean;
  canDelete: boolean;
  reason?: string;
}

/**
 * 슬롯 상태에 따른 권한을 확인합니다.
 */
export function getSlotPermissions(
  slot: Slot,
  currentUser: User | null,
  isRestricted: boolean = false
): SlotPermissionResult {
  if (!currentUser) {
    return {
      canExtend: false,
      canEdit: false,
      canBatchEdit: false,
      canRefund: false,
      canDelete: false,
      reason: '로그인이 필요합니다.'
    };
  }

  const isAdmin = currentUser.grade === 'ADMIN';
  const isSeller = currentUser.grade === 'SELLER';
  const todayStart = startOfTodayLocal();
  const startMid = parseCalendarDate(slot.start_date);
  const endMid = parseCalendarDate(slot.end_date);

  const isExpiredToday =
    !!endMid && isSameLocalCalendarDay(endMid, new Date());
  // 시작일이 오늘(로컬 날짜) 이후면 미개시
  const isNotStarted = !!startMid && startMid.getTime() > todayStart.getTime();
  // 작동 중: 시작일 ≤ 오늘 이고 종료일(날짜) > 오늘
  const isActive =
    !!startMid &&
    !!endMid &&
    startMid.getTime() <= todayStart.getTime() &&
    endMid.getTime() > todayStart.getTime();

  // 시간 제한 확인
  const editTimeRestricted = isSlotOperationRestricted(currentUser, '수정');
  const extendTimeRestricted = isSlotOperationRestricted(currentUser, '연장');
  const refundTimeRestricted = isSlotOperationRestricted(currentUser, '환불');
  const deleteTimeRestricted = isSlotOperationRestricted(currentUser, '삭제');
  // 셀러는 연장/환불 UseCase에서 차단됨 → 버튼도 비활성
  const canSellerMutateOps = !isSeller;
  // adpa99 쪽만 이미 시작된 슬롯도 (환불처럼) 삭제 가능
  const canDeleteStarted =
    isAdpa99Username(currentUser.username) && !isSeller && !deleteTimeRestricted && !isRestricted;

  switch (slot.status) {
    case '정상':
      // 미개시 정상 슬롯은 조건에 따라 삭제 가능
      if (isNotStarted) {
        return {
          canExtend: canSellerMutateOps && !extendTimeRestricted && !isRestricted,
          canEdit: !editTimeRestricted,
          canBatchEdit: !editTimeRestricted,
          canRefund: false, // 미개시 슬롯은 환불 불가
          // 삭제: 일반 ADMIN은 시간 제한 없음(도메인상 deleteTimeRestricted=false). 제한형 ADMIN·그 외는 시간·외부 제한 반영
          canDelete:
            (isAdmin && !deleteTimeRestricted) ||
            (!isSeller && !isAdmin && !deleteTimeRestricted && !isRestricted),
          reason: isSeller
            ? '셀러는 연장 권한이 없습니다.'
            : editTimeRestricted
              ? getRestrictionMessage('수정', currentUser)
              : undefined
        };
      }
      // 시작된 정상 슬롯은 원칙적으로 삭제 불가. 단, adpa99는 환불처럼 삭제 가능
      return {
        canExtend: canSellerMutateOps && !extendTimeRestricted && !isRestricted,
        canEdit: !editTimeRestricted,
        canBatchEdit: !editTimeRestricted,
        canRefund: canSellerMutateOps && !refundTimeRestricted && !isRestricted,
        canDelete: canDeleteStarted,
        reason: isSeller
          ? '셀러는 연장/환불 권한이 없습니다.'
          : editTimeRestricted
            ? getRestrictionMessage('수정', currentUser)
            : isActive && !canDeleteStarted
              ? '작동 중인 슬롯은 삭제할 수 없습니다.'
              : undefined
      };

    case '연장':
      // 미개시 연장 슬롯은 조건에 따라 삭제 가능
      if (isNotStarted) {
        return {
          canExtend: canSellerMutateOps && !extendTimeRestricted && !isRestricted,
          canEdit: !editTimeRestricted,
          canBatchEdit: !editTimeRestricted,
          canRefund: false, // 미개시 슬롯은 환불 불가
          canDelete:
            (isAdmin && !deleteTimeRestricted) ||
            (!isSeller && !isAdmin && !deleteTimeRestricted && !isRestricted),
          reason: isSeller
            ? '셀러는 연장 권한이 없습니다.'
            : editTimeRestricted
              ? getRestrictionMessage('수정', currentUser)
              : undefined
        };
      }
      return {
        canExtend: canSellerMutateOps && !extendTimeRestricted && !isRestricted,
        canEdit: !editTimeRestricted,
        canBatchEdit: !editTimeRestricted,
        canRefund: canSellerMutateOps && !refundTimeRestricted && !isRestricted,
        canDelete: canDeleteStarted,
        reason: isSeller
          ? '셀러는 연장/환불 권한이 없습니다.'
          : editTimeRestricted
            ? getRestrictionMessage('수정', currentUser)
            : isActive && !canDeleteStarted
              ? '작동 중인 슬롯은 삭제할 수 없습니다.'
              : undefined
      };

    case '환불':
      return {
        canExtend:
          canSellerMutateOps &&
          isExpiredToday &&
          !extendTimeRestricted &&
          !isRestricted,
        canEdit: false,
        canBatchEdit: false,
        canRefund: false,
        // 정책: 미개시 정상 슬롯만 삭제 가능 → 환불 상태 삭제 불가
        canDelete: false,
        reason: isSeller
          ? '셀러는 연장 권한이 없습니다.'
          : isExpiredToday && (extendTimeRestricted || isRestricted)
            ? getRestrictionMessage('연장', currentUser)
            : !isExpiredToday
              ? '환불된 슬롯은 종료일이 오늘인 경우에만 연장 가능합니다.'
              : undefined
      };

    case '종료':
      return {
        canExtend: false,
        canEdit: false,
        canBatchEdit: false,
        canRefund: false,
        // 종료된 슬롯은 ADMIN만 삭제 가능(제한형은 허용 시간 내)
        canDelete: isAdmin && !deleteTimeRestricted,
        reason: deleteTimeRestricted
          ? getRestrictionMessage('삭제', currentUser)
          : '종료된 슬롯은 삭제만 가능합니다.',
      };

    default:
      return {
        canExtend: false,
        canEdit: false,
        canBatchEdit: false,
        canRefund: false,
        canDelete: false,
        reason: '알 수 없는 상태입니다.'
      };
  }
}

/**
 * 선택된 슬롯들에 대한 일괄 권한을 확인합니다.
 */
export function getBatchPermissions(
  slots: Slot[],
  currentUser: User | null,
  isRestricted: boolean = false
): {
  canExtend: boolean;
  canEdit: boolean;
  canBatchEdit: boolean;
  canRefund: boolean;
  canDelete: boolean;
  hasRefunded: boolean;
  hasNotStarted: boolean;
  reason?: string;
} {
  if (slots.length === 0) {
    return {
      canExtend: false,
      canEdit: false,
      canBatchEdit: false,
      canRefund: false,
      canDelete: false,
      hasRefunded: false,
      hasNotStarted: false,
      reason: '선택된 슬롯이 없습니다.'
    };
  }

  // 각 권한별로 확인
  const permissions = slots.map(slot => getSlotPermissions(slot, currentUser, isRestricted));

  const canExtend = permissions.every(p => p.canExtend);
  const canEdit = permissions.every(p => p.canEdit);
  const canBatchEdit = permissions.every(p => p.canBatchEdit);
  let canRefund = permissions.every(p => p.canRefund);
  const canDelete = permissions.every(p => p.canDelete);

  // 환불된 슬롯이 있는지 확인
  const hasRefunded = slots.some(slot => slot.status === '환불');
  
  // 미개시 슬롯이 있는지 확인 (시작일이 오늘 이후인 슬롯)
  const todayStart = startOfTodayLocal();

  const hasNotStarted = slots.some(slot => {
    if (!slot.start_date) return false;
    const startMid = parseCalendarDate(slot.start_date);
    return !!startMid && startMid.getTime() > todayStart.getTime();
  });

  const hasActive = slots.some(slot => {
    if (!slot.start_date || !slot.end_date) return false;
    const startMid = parseCalendarDate(slot.start_date);
    const endMid = parseCalendarDate(slot.end_date);
    if (!startMid || !endMid) return false;
    return (
      startMid.getTime() <= todayStart.getTime() && endMid.getTime() > todayStart.getTime()
    );
  });

  // 미개시 슬롯 포함 시 환불 불가 처리 (사유 메시지는 환불 문맥에서만 표시해야 하므로 여기서는 설정하지 않음)
  if (hasNotStarted) {
    canRefund = false;
  }

  let reason: string | undefined;
  if (hasRefunded && !canRefund) {
    reason = '환불된 슬롯이 포함되어 있어 환불이 불가능합니다.';
  } else if (hasActive && !canDelete) {
    reason = '작동 중인 슬롯이 포함되어 있어 삭제가 불가능합니다.';
  }

  return {
    canExtend,
    canEdit,
    canBatchEdit,
    canRefund,
    canDelete,
    hasRefunded,
    hasNotStarted,
    reason
  };
}

/**
 * 슬롯이 삭제 가능한지 확인합니다.
 */
export function canDeleteSlot(slot: Slot, currentUser: User | null): boolean {
  if (!currentUser) {
    return false;
  }

  // 종료된 슬롯: ADMIN만 삭제 가능(제한형은 허용 시간 내)
  if (slot.status === '종료') {
    return (
      currentUser.grade === 'ADMIN' &&
      !isSlotOperationRestricted(currentUser, '삭제')
    );
  }

  // 정책: 미개시 정상 또는 연장 슬롯만 삭제 가능
  if (slot.status !== '정상' && slot.status !== '연장') return false;
  if (!slot.start_date) return false;

  const todayStart = startOfTodayLocal();
  const startMid = parseCalendarDate(slot.start_date);
  // adpa99는 이미 시작된 슬롯도 삭제 가능 (셀러/시간제한 제외)
  if (startMid && startMid.getTime() <= todayStart.getTime()) {
    if (!isAdpa99Username(currentUser.username)) return false;
    if (currentUser.grade === 'SELLER') return false;
    if (isSlotOperationRestricted(currentUser, '삭제')) return false;
    return true;
  }
  if (!startMid || !(startMid.getTime() > todayStart.getTime())) return false;

  // ADMIN: 미개시 정상/연장 삭제 — 제한형은 허용 시간 내에만
  if (currentUser.grade === 'ADMIN') {
    return !isSlotOperationRestricted(currentUser, '삭제');
  }

  // SELLER: 삭제 불가
  if (currentUser.grade === 'SELLER') {
    return false;
  }

  // 대행사/총판: 제한 시간 외에만 가능
  const timeRestricted = isSlotOperationRestricted(currentUser, '삭제');
  if (timeRestricted) return false;

  return true;
}

/**
 * 슬롯이 연장 가능한지 확인합니다.
 */
export function canExtendSlot(slot: Slot, currentUser: User | null): boolean {
  if (!currentUser) return false;
  if (currentUser.grade === 'SELLER') return false;

  const timeRestricted = isSlotOperationRestricted(currentUser, '연장');
  if (timeRestricted) return false;

  switch (slot.status) {
    case '정상':
    case '연장':
      return true;
    case '환불': {
      const endMid = parseCalendarDate(slot.end_date);
      return !!endMid && isSameLocalCalendarDay(endMid, new Date());
    }
    case '종료':
      return false;
    default:
      return false;
  }
}

/**
 * 슬롯이 시작 전인지 확인
 * @param slot 슬롯 객체
 * @returns 시작 전이면 true, 시작했으면 false
 */
export function isSlotBeforeStart(slot: Slot): boolean {
  if (!slot.start_date) return false;
  
  const todayStart = startOfTodayLocal();
  const startMid = parseCalendarDate(slot.start_date);
  if (!startMid) return false;

  return todayStart.getTime() < startMid.getTime();
}

/**
 * 슬롯이 환불 가능한지 확인
 * @param slot 슬롯 객체
 * @returns 환불 가능하면 true, 불가능하면 false
 */
export function canRefundSlot(slot: Slot): boolean {
  // 시작 전 슬롯은 환불 불가
  if (isSlotBeforeStart(slot)) {
    return false;
  }
  
  // 이미 환불된 슬롯은 환불 불가
  if (slot.status === '환불') {
    return false;
  }
  
  return true;
}
